// AccentLengthImperialUnbound.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { debounce } from 'perfect-debounce';
import { accentUtils, from } from '../../services/HelperService';
import { AccentLabel } from '../AccentLabel';
import { AccentComboUnbound } from './AccentComboUnbound';
import { toLength, fromDecimal, generateFractions } from '../products/ProductConfiguratorShared';

/**
 * Helper function to split the defaultValue into yards, feet, inches, and fraction
 * based on the defaultUnit.
 * 
 * @param {number} value - The defaultValue in the specified unit.
 * @param {number} unit - The unit type (9144 for yards, 3048 for feet, 254 for inches).
 * @param {number} precision - The precision for fractions.
 * @returns {object} - An object containing yards, feet, inches, and fraction.
 */
const splitValue = (value, unit, precision) => {
    let yards = 0, feet = 0, inches = 0, fraction = 0;

    switch (unit) {
        case 9144: // Yards
            yards = Math.trunc(value);
            let remainderYards = (value - yards) * 3; // 1 yard = 3 feet
            feet = Math.trunc(remainderYards);
            let remainderFeet = (remainderYards - feet) * 12; // 1 foot = 12 inches
            inches = Math.trunc(remainderFeet);
            fraction = Math.round((remainderFeet - inches) * precision) / precision;
            break;
        case 3048: // Feet
            feet = Math.trunc(value);
            let remainderFeetOnly = (value - feet) * 12; // 1 foot = 12 inches
            inches = Math.trunc(remainderFeetOnly);
            fraction = Math.round((remainderFeetOnly - inches) * precision) / precision;
            break;
        case 254: // Inches
        default:
            inches = Math.trunc(value);
            fraction = Math.round((value - inches) * precision) / precision;
            break;
    }

    return { yards, feet, inches, fraction };
};

const generateNegativeFractions = (denominator) => {
    return generateFractions(denominator)
        .filter(x => x.ID > 0)
        .map(x => ({
            ID: -x.ID,
            Description: `-${x.Description}`
        }))
        .reverse();
};


export const AccentLengthImperialUnbound = ({
    defaultValue = 0,
    defaultUnit,
    onChange,
    onBlur,
    onFocus,
    min = 0,
    max = 1000, // Set a sensible default max if not provided
    debounceMilliseconds,
    className = '',
    label,
    required = false,
    invalid = false,
    disabled = false,
    placeHolder,
    step = 1,
    precision = 16, // 1/16
    dataTagName
}) => {

    if (isNaN(defaultUnit)) {
        throw new Error("defaultUnit must be a Number, not an enum");
    }

    // Split the defaultValue based on the defaultUnit
    const initialValues = splitValue(defaultValue, defaultUnit, precision);

    

    // Initialize state based on split values
    const [yards, setYards] = useState(initialValues.yards);
    const [feet, setFeet] = useState(initialValues.feet);
    const [inches, setInches] = useState(initialValues.inches);
    const [fraction, setFraction] = useState(initialValues.fraction);
    const lastValidValuesRef = useRef(initialValues);
    const editStartValuesRef = useRef(initialValues);


    // Create a ref for the container
    const containerRef = useRef(null);

    // Handle container-level blur
    const handleContainerBlur = (e) => {
        // Check if the new focus is outside the container
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
            handleValidatedBlur();
        }
    };

    // Handle container-level focus
    const handleContainerFocus = () => {
        if (onFocus) {
            const focusedValue = calculateTotalValue();
            onFocus(focusedValue);
        }
    };


    // Debounced notifyChange function
    const notifyChange = useRef(debounce((newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    }, debounceMilliseconds || 500)).current;

    useEffect(() => {
        // Cleanup debounce on unmount
        return () => {
            notifyChange.cancel && notifyChange.cancel();
        };
    }, [notifyChange]);

    const unitDisplayConfig = () => {
        switch (defaultUnit) {
            case 9144:
                return { showYards: true, showFeet: true, showInches: true };
            case 3048:
                return { showYards: false, showFeet: true, showInches: true };
            case 254:
                return { showYards: false, showFeet: false, showInches: true };
            default:
                return { showYards: false, showFeet: false, showInches: true };
        }
    };

    const { showYards, showFeet, showInches } = unitDisplayConfig();

    const getValueSign = (values) => {
        if (showYards && values.yards !== 0) return Math.sign(values.yards);
        if (showFeet && values.feet !== 0) return Math.sign(values.feet);
        if (showInches && values.inches !== 0) return Math.sign(values.inches);
        if (values.fraction !== 0) return Math.sign(values.fraction);

        return 1;
    };

    const getLargerUnitSign = (unit, values) => {
        if (unit !== "yards" && showYards && values.yards !== 0) return Math.sign(values.yards);
        if (unit !== "yards" && unit !== "feet" && showFeet && values.feet !== 0) return Math.sign(values.feet);
        if (unit === "fraction" && showInches && values.inches !== 0) return Math.sign(values.inches);

        return 0;
    };

    const applySign = (value, sign) => {
        if (accentUtils.isNull(value) || value === 0 || sign === 0) return value;

        return sign * Math.abs(value);
    };

    const applySignToLowerUnits = (values, unit, sign) => {
        if (sign === 0) return values;

        if (unit === "yards") {
            return {
                ...values,
                feet: applySign(values.feet, sign),
                inches: applySign(values.inches, sign),
                fraction: applySign(values.fraction, sign)
            };
        }

        if (unit === "feet") {
            return {
                ...values,
                inches: applySign(values.inches, sign),
                fraction: applySign(values.fraction, sign)
            };
        }

        if (unit === "inches") {
            return {
                ...values,
                fraction: applySign(values.fraction, sign)
            };
        }

        return values;
    };

    const calculateTotalValue = (overrides = {}) => {
        // Spread current state values first, then overrides
        const values = {
            yards,
            feet,
            inches,
            fraction,
            ...overrides
        };

        const sign = getValueSign(values);
        const absYards = Math.abs(values.yards ?? 0);
        const absFeet = Math.abs(values.feet ?? 0);
        const absInches = Math.abs(values.inches ?? 0);
        const absFraction = Math.abs(values.fraction ?? 0);

        switch (defaultUnit) {
            case 9144: // Yards
                return toLength(sign * (absYards + (absFeet / 3) + (absInches / 36) + (absFraction / 36)), defaultUnit);
            case 3048: // Feet
                return toLength(sign * (absFeet + (absInches / 12) + (absFraction / 12)), defaultUnit);
            case 254: // Inches
            default:
                return toLength(sign * (absInches + absFraction), defaultUnit);
        }
    };

    const getCurrentValues = () => ({ yards, feet, inches, fraction });

    const captureEditStart = () => {
        editStartValuesRef.current = getCurrentValues();
    };

    const setAllValues = (values) => {
        setYards(values.yards);
        setFeet(values.feet);
        setInches(values.inches);
        setFraction(values.fraction);
    };

    const isWithinOverallBounds = (length) => {
        const value = fromDecimal(length?.Value);

        if (!accentUtils.isNull(min) && value < min) return false;
        if (!accentUtils.isNull(max) && value > max) return false;

        return true;
    };

    const clampNonNegativeIfRequired = (value) => {
        if (!accentUtils.isNull(min) && min >= 0 && value < 0) return 0;

        return value;
    };

    const getSubControlBounds = (unit, baseMax) => {
        const largerUnitSign = getLargerUnitSign(unit, { yards, feet, inches, fraction });

        if (largerUnitSign < 0) {
            return {
                min: accentUtils.isNull(baseMax) ? undefined : -baseMax,
                max: 0
            };
        }

        if (largerUnitSign > 0) {
            return {
                min: 0,
                max: baseMax
            };
        }

        return {
            min: accentUtils.isNull(min) || min < 0 ? undefined : 0,
            max: baseMax
        };
    };

    const isStepChange = (previousValue, nextValue, step = 1) => {
        if (accentUtils.isNull(previousValue) || accentUtils.isNull(nextValue)) return false;

        return Math.abs(Math.abs(nextValue) - Math.abs(previousValue)) === step;
    };

    const recordAndNotifyIfValid = (values, { updateRestorePoint = false } = {}) => {
        const length = calculateTotalValue(values);

        if (!isWithinOverallBounds(length)) return null;

        if (updateRestorePoint) {
            editStartValuesRef.current = values;
        }

        notifyChange(length);
        return length;
    };

    const handleValidatedBlur = () => {
        const values = getCurrentValues();
        const length = calculateTotalValue(values);

        if (!isWithinOverallBounds(length)) {
            const restoreValues = editStartValuesRef.current ?? lastValidValuesRef.current;
            setAllValues(restoreValues);

            if (onBlur) {
                onBlur(calculateTotalValue(restoreValues));
            }

            return;
        }

        lastValidValuesRef.current = values;
        editStartValuesRef.current = values;

        if (onBlur) {
            onBlur(length);
        }
    };

   
    // Handlers for input changes
    const handleYardsChange = (e) => {
        const previousYards = yards;
        let newYards = e.value;
        newYards = clampNonNegativeIfRequired(newYards);

        const normalized = applySignToLowerUnits({
            yards: newYards ?? 0,
            feet,
            inches,
            fraction
        }, "yards", Math.sign(newYards ?? 0));

        setYards(normalized.yards);
        setFeet(normalized.feet);
        setInches(normalized.inches);
        setFraction(normalized.fraction);

        recordAndNotifyIfValid(normalized, {
            updateRestorePoint: isStepChange(previousYards, normalized.yards)
        });
    };

    const handleFeetChange = (e) => {
        const previousFeet = feet;
        let newFeet = e.value;
        newFeet = clampNonNegativeIfRequired(newFeet);

        const largerUnitSign = getLargerUnitSign("feet", { yards, feet: newFeet ?? 0, inches, fraction });
        newFeet = applySign(newFeet, largerUnitSign);

        const normalized = applySignToLowerUnits({
            yards,
            feet: newFeet ?? 0,
            inches,
            fraction
        }, "feet", largerUnitSign || Math.sign(newFeet ?? 0));

        setFeet(normalized.feet);
        setInches(normalized.inches);
        setFraction(normalized.fraction);

        recordAndNotifyIfValid(normalized, {
            updateRestorePoint: isStepChange(previousFeet, normalized.feet)
        });
    };

    const handleInchesChange = (e) => {
        const previousInches = inches;
        let newInches = e.value;
        newInches = clampNonNegativeIfRequired(newInches);

        // Only restrict to 11 inches if defaultUnit is not 254 (Inches)
        if (defaultUnit !== 254) {
            newInches = newInches > 11 ? 11 : newInches; // Prevent exceeding 11 inches
        }

        const largerUnitSign = getLargerUnitSign("inches", { yards, feet, inches: newInches ?? 0, fraction });
        newInches = applySign(newInches, largerUnitSign);

        const normalized = applySignToLowerUnits({
            yards,
            feet,
            inches: newInches ?? 0,
            fraction
        }, "inches", largerUnitSign || Math.sign(newInches ?? 0));

        setInches(normalized.inches);
        setFraction(normalized.fraction);

        recordAndNotifyIfValid(normalized, {
            updateRestorePoint: isStepChange(previousInches, normalized.inches)
        });
    };

    const handleFractionChange = (value) => {
        const largerUnitSign = getLargerUnitSign("fraction", { yards, feet, inches, fraction: value ?? 0 });
        const newFraction = applySign(value, largerUnitSign);

        setFraction(newFraction);

        const total = recordAndNotifyIfValid({ yards, feet, inches, fraction: newFraction ?? 0 });
        if (total && onBlur) {
            onBlur(total);
        }
    };



    // Generate fractions using provided helper function
    const zeroFraction = { ID: 0, Description: "0" };
    const positiveFractions = generateFractions(precision || 16).filter(x => x.ID > 0);
    const negativeFractions = generateNegativeFractions(precision || 16);
    const allowsNegativeValues = accentUtils.isNull(min) || min < 0;
    const wholePartSign = getValueSign({ yards, feet, inches, fraction: 0 });
    const shouldIncludeNegativeFractions = allowsNegativeValues || wholePartSign < 0 || fraction < 0;
    const hasWholePart = (showYards && yards !== 0) || (showFeet && feet !== 0) || (showInches && inches !== 0);
    const fractions = !shouldIncludeNegativeFractions
        ? [zeroFraction, ...positiveFractions]
        : !hasWholePart
            ? [...negativeFractions, zeroFraction, ...positiveFractions]
            : wholePartSign < 0
                ? [...negativeFractions, zeroFraction]
                : [zeroFraction, ...positiveFractions];
    const fractionSignKey = Math.sign(fraction || 0);

    // Validation logic
    const isNotValid = required && (
        (showYards && accentUtils.isNull(yards)) ||
        (showFeet && accentUtils.isNull(feet)) ||
        (showInches && accentUtils.isNull(inches)) ||
        (showInches && accentUtils.isNull(fraction)) ||
        !!invalid
    );
    const inputClassName = isNotValid ? `accentNumberUnbound invalid ${className}` : `accentNumberUnbound ${className}`;

    // Styles for individual inputs
    const inputStyle = {};
    const fractionStyle = {};
    const primaryUnitBounds = {
        min: accentUtils.isNull(min) ? undefined : min,
        max: accentUtils.isNull(max) ? undefined : max
    };
    const yardsBounds = defaultUnit === 9144 ? primaryUnitBounds : getSubControlBounds("yards");
    const feetBounds = defaultUnit === 3048
        ? primaryUnitBounds
        : getSubControlBounds("feet", defaultUnit === 9144 ? 2 : undefined);
    const inchesBounds = getSubControlBounds(
        "inches",
        defaultUnit !== 254 ? 11 : undefined
    );

    const hasFractions = from(fractions).any(x => x.ID !== 0);

    return (
        <AccentLabel text={label}>
            <div
                ref={containerRef}
                className="d-flex"
                style={{ width: '100%' }}
                data-tagname={dataTagName}
                onBlur={handleContainerBlur}
                onFocus={handleContainerFocus}
                tabIndex={-1} // Make the div focusable to capture focus events
            >
                {showYards && (
                    <div style={{ flex: '1 1 auto', marginRight: '8px', minWidth: '100px' }}>
                        {/* Yards Input */}
                        <NumericTextBox
                            value={yards}
                            onChange={handleYardsChange}
                            disabled={disabled}
                            onFocus={e => {
                                captureEditStart();
                                e.target.element.select();
                            }}
                            min={yardsBounds.min}
                            max={yardsBounds.max}
                            step={1}
                            className={inputClassName}
                            style={inputStyle}
                            format="# yd"
                            placeholder="Yards"
                        />
                    </div>
                )}
                {showFeet && (
                    <div style={{ flex: '1 1 auto', marginRight: '8px', minWidth: '100px' }}>
                        {/* Feet Input */}
                        <NumericTextBox
                            value={feet}
                            onChange={handleFeetChange}
                            disabled={disabled}
                            onFocus={e => {
                                captureEditStart();
                                e.target.element.select();
                            }}
                            min={feetBounds.min}
                            max={feetBounds.max}
                            step={1}
                            className={inputClassName}
                            style={inputStyle}
                            format="# ft"
                            placeholder="Feet"
                        />
                    </div>
                )}
                {showInches && (
                    <div style={{ flex: '1 1 auto', marginRight: '8px', minWidth: '100px' }}>
                        {/* Inches Input */}
                        <NumericTextBox
                            value={inches}
                            onChange={handleInchesChange}
                            disabled={disabled}
                            onFocus={e => {
                                captureEditStart();
                                e.target.element.select();
                            }}
                            min={inchesBounds.min}
                            max={inchesBounds.max}
                            step={1}
                            className={inputClassName}
                            style={inputStyle}
                            format="# in"
                            placeholder="Inches"
                        />
                    </div>
                )}
                {showInches && hasFractions && (
                    <div style={{ flex: '1 1 auto', minWidth:'120px' }}>
                        {/* Fraction Input */}
                        <AccentComboUnbound
                            key={`fraction-${fractionSignKey}`}
                            defaultValue={fraction}
                            onChange={handleFractionChange}
                            data={fractions}
                            displayField="Description"
                            valueField="ID"
                            disabled={disabled}
                            style={fractionStyle}
                            ariaLabel="Fractional Inches"
                        />
                    </div>
                )}
            </div>
        </AccentLabel>
    );
};

AccentLengthImperialUnbound.propTypes = {
    defaultUnit: PropTypes.oneOf([
        254,  // Inches
        3048, // Feet
        9144  // Yards
    ]).isRequired,
    defaultValue: PropTypes.number, // Changed from object to number
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    debounceMilliseconds: PropTypes.number,
    className: PropTypes.string,
    label: PropTypes.string,
    required: PropTypes.bool,
    invalid: PropTypes.bool,
    disabled: PropTypes.bool,
    placeHolder: PropTypes.string,
    precision: PropTypes.number,
    dataTagName: PropTypes.string
};
